/**
 * Date and time utility functions
 */

export class DateUtil {
  /**
   * Parse date string in DD-MM-YYYY format to Date object
   */
  static parseDateString(dateStr: string | Date): Date {
    if (dateStr instanceof Date) {
      return dateStr;
    }
    
    // Check if it's DD-MM-YYYY format
    if (dateStr.includes('-') && dateStr.length === 10) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        // DD-MM-YYYY format
        const [day, month, year] = parts;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    
    // Fall back to standard Date parsing
    return new Date(dateStr);
  }

  /**
   * Format date to DD-MM-YYYY format
   */
  static formatToStandardFormat(date: string | Date): string {
    const dateObj = this.parseDateString(date);
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /**
   * Format time to 12-hour format with AM/PM
   */
  static formatTo12Hour(time: string): string {
    if (time.includes('AM') || time.includes('PM')) {
      return time; // Already in 12-hour format
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Formats a date range. If start and end dates are the same day, returns single date.
   * Otherwise returns a date range string.
   */
  static formatDateRange(
    startDate: string | Date, 
    endDate: string | Date,
    locale?: string
  ): string {
    const start = this.parseDateString(startDate);
    const end = this.parseDateString(endDate);

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString(locale);
    } else {
      return `${start.toLocaleDateString(locale)} - ${end.toLocaleDateString(locale)}`;
    }
  }

  /**
   * Formats a single date
   */
  static formatDate(date: string | Date, locale?: string): string {
    const dateObj = this.parseDateString(date);
    return dateObj.toLocaleDateString(locale);
  }

  /**
   * Formats a time range string
   */
  static formatTimeRange(
    startTime: string, 
    endTime: string,
    locale?: string
  ): string {
    const formatOptions: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    };

    // Handle 12-hour format times
    const parseTime = (time: string) => {
      if (time.includes('AM') || time.includes('PM')) {
        const [timePart, period] = time.split(' ');
        const [hours, minutes] = timePart.split(':').map(Number);
        let hour24 = hours;
        
        if (period === 'PM' && hours !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hours === 12) {
          hour24 = 0;
        }
        
        return new Date(`2000-01-01T${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      }
      
      return new Date(`2000-01-01T${time}`);
    };

    const startTimeObj = parseTime(startTime);
    const endTimeObj = parseTime(endTime);

    return `${startTimeObj.toLocaleTimeString(locale, formatOptions)} - ${endTimeObj.toLocaleTimeString(locale, formatOptions)}`;
  }

  /**
   * Formats a single time string
   */
  static formatTime(time: string, locale?: string): string {
    const formatOptions: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    };

    // Handle 12-hour format times
    if (time.includes('AM') || time.includes('PM')) {
      const [timePart, period] = time.split(' ');
      const [hours, minutes] = timePart.split(':').map(Number);
      let hour24 = hours;
      
      if (period === 'PM' && hours !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }
      
      const timeObj = new Date(`2000-01-01T${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      return timeObj.toLocaleTimeString(locale, formatOptions);
    }

    const timeObj = new Date(`2000-01-01T${time}`);
    return timeObj.toLocaleTimeString(locale, formatOptions);
  }

  /**
   * Checks if a date is in the past
   */
  static isPast(date: string | Date): boolean {
    const dateObj = this.parseDateString(date);
    const now = new Date();
    return dateObj < now;
  }

  /**
   * Checks if a date is in the future
   */
  static isFuture(date: string | Date): boolean {
    const dateObj = this.parseDateString(date);
    const now = new Date();
    return dateObj > now;
  }

  /**
   * Checks if a date is today
   */
  static isToday(date: string | Date): boolean {
    const dateObj = this.parseDateString(date);
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  }

  /**
   * Gets the number of days between two dates
   */
  static getDaysBetween(startDate: string | Date, endDate: string | Date): number {
    const start = this.parseDateString(startDate);
    const end = this.parseDateString(endDate);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Gets the number of days until a future date
   */
  static getDaysUntil(date: string | Date): number {
    const targetDate = this.parseDateString(date);
    const today = new Date();
    const diffTime = targetDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
}